import { ArrowRight, FileDown } from 'lucide-react';
import { getPublishedResources, RESOURCE_TYPES, topicFor } from '@/data/insights';
import { Reveal } from '../Reveal';

// Section 5 — Guides & practical resources.
// The data structure and finished layout are ready, but the section HIDES
// itself when no genuine published resources exist. No fake download
// buttons, no "coming soon" cards. Gated resources will collect name,
// business name, email, resource requested, marketing consent and
// source/campaign details through the existing secure enquiry service.
export const ResourcesSection = () => {
  const resources = getPublishedResources();
  if (resources.length === 0) return null;

  return (
    <section aria-labelledby="resources-heading" data-testid="insights-resources" className="bg-mist py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="flex items-center gap-3">
            <span className="font-mono text-xs font-medium tracking-widest text-gold-700">03</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">
              Guides &amp; Resources
            </span>
          </p>
          <h2 id="resources-heading" data-testid="resources-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
            Practical tools you can use
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
            Explore useful guides, checklists and resources designed to help business owners and
            managers take practical action.
          </p>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="resources-grid">
          {resources.map((res, i) => {
            const topic = res.topic ? topicFor(res.topic) : null;
            return (
              <Reveal key={res.id} delay={(i % 3) * 0.08} className="h-full">
                <article
                  data-testid={`resource-card-${res.slug}`}
                  className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-navy-900 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
                      <FileDown className="h-3 w-3" aria-hidden="true" />
                      {RESOURCE_TYPES[res.resourceType] || 'Resource'}
                    </span>
                    {topic && (
                      <span
                        className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
                        style={{ backgroundColor: topic.soft, color: topic.dark }}
                      >
                        {topic.label}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-4 text-lg font-extrabold leading-snug tracking-tight text-navy-900">
                    {res.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{res.description}</p>
                  <div className="mt-auto pt-5">
                    <a
                      href={res.fileUrl}
                      data-testid={`resource-action-${res.slug}`}
                      className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-navy-900 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-700"
                    >
                      {res.requiresForm ? 'Request This Resource' : 'Download'}
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
                    </a>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
